import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { SITE } from '../mock/data';
import { publicApi } from '../api';
import { useLang } from '../i18n';
import { toAbsoluteMediaUrl } from '../lib/media';

/**
 * Standalone landing reachable at /freeversion/[slug].
 * URL is printed on physical QR codes — slug must stay stable.
 * Background image + destination button are configured in Admin CMS.
 */
export default function FreeVersion() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound

  useEffect(() => {
    if (!slug) return;
    setStatus('loading');
    publicApi
      .qrPage(slug)
      .then((p) => {
        setPage(p);
        setStatus('ok');
      })
      .catch(() => setStatus('notfound'));
  }, [slug]);

  const handleClick = (e) => {
    if (!page?.destination_url) return;
    const url = page.destination_url;
    // External URL → open in same tab; internal → SPA navigate.
    if (/^https?:\/\//i.test(url)) {
      e.preventDefault();
      window.location.href = url;
    } else {
      e.preventDefault();
      nav(url);
    }
  };

  if (status === 'loading') {
    return (
      <div
        className="w-full flex items-center justify-center text-white/70 text-sm"
        style={{ minHeight: '100vh', background: '#000' }}
        data-testid="freeversion-loading"
      >
        {t('loading')}
      </div>
    );
  }

  if (status === 'notfound') {
    return (
      <div
        className="w-full flex flex-col items-center justify-center text-center text-white p-6"
        style={{ minHeight: '100vh', background: '#000' }}
        data-testid="freeversion-notfound"
      >
        <img src={SITE.logo} alt="YUP" className="h-24 w-auto mb-6 opacity-90" />
        <h1 className="text-2xl tagline-display" style={{ color: 'var(--yup-primary)' }}>
          {t('pageNotFound')}
        </h1>
        <p className="mt-2 text-white/60 text-sm">{t('qrNotFoundDesc')}</p>
        <Link to="/" className="btn-primary mt-8 inline-block">
          {t('goToSite')}
        </Link>
      </div>
    );
  }

  // Wrap URL in quotes so any special chars (parentheses, spaces) don't break CSS url() parsing.
  const bgAbs = toAbsoluteMediaUrl(page.background_image || SITE.heroBg);
  const bgCss = `url("${bgAbs}")`;

  return (
    <div
      className="relative w-full"
      style={{ minHeight: '100vh' }}
      data-testid="freeversion-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: bgCss }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/40 to-black/80" />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-6 py-16 text-center text-white">
        <img
          src={SITE.logo}
          alt="YUP logo"
          className="h-28 md:h-40 w-auto drop-shadow-2xl fade-in-up"
          data-testid="freeversion-logo"
        />

        {page.title && (
          <h1
            className="mt-8 text-3xl md:text-5xl tagline-display fade-in-up fade-in-up-delay-1 max-w-3xl"
            style={{ color: 'var(--yup-primary)' }}
          >
            {page.title}
          </h1>
        )}

        <div className="mt-16 fade-in-up fade-in-up-delay-2">
          <a
            href={page.destination_url}
            onClick={handleClick}
            className="btn-primary text-lg md:text-xl px-10 py-4 tracking-[0.2em]"
            data-testid="freeversion-cta-btn"
          >
            {t('clickHere')}
          </a>
        </div>
      </div>
    </div>
  );
}
